<template>
  <div class="space-y-4">
    <h3 class="uppercase font-medium text-lg">details</h3>
    <ValidationObserver
      v-slot="{ handleSubmit, invalid }"
      ref="observer"
      tag="div"
      class="space-y-12"
    >
      <BaseCard class="border-0">
        <BaseCardBody>
          <div class="p-3">
            <DetailsForm
              :submitting="submitting"
              :form-data="data"
              :statuses="included.statuses"
              :tags="included.tags"
              :assignees="included.assignees"
              :contact-point-users="included.contactPointUsers"
              :supplier-options="supplierOptions"
              @changed="updateOrderData"
            />
          </div>
        </BaseCardBody>
      </BaseCard>

      <ConvertOrderToInvoiceForm v-if="order && order.uuid" :order-id="order.uuid" />

      <div class="space-y-4">
        <h3 class="uppercase font-medium text-lg">order items</h3>

        <OrderFormOrderItemsToolbar
          :specs-visible.sync="specsVisible"
          @add-line="addLine"
          @add-product="addProduct"
          @add-order-lines="addOrderLines"
        />

        <div v-if="data.orderItems">
          <template v-for="(orderItem, orderItemIdx) in data.orderItems">
            <div
              class="mb-2"
              v-if="!orderItem.parentOrderItemUuid && !orderItem.tempParentId"
              :key="orderItemIdx"
            >
              <OrderItem
                :default-project-data="{ name: data.name }"
                :seller-account-id="sellerAccountId"
                :customer-account-id="customerAccountId"
                :order-item="orderItem"
                :requirements.sync="orderItem.requirements"
                :order-items="data.orderItems"
                :lock-edit="submitting"
                :specs-visible="specsVisible"
                :has-project="hasProject"
                @add-line="addLine"
                @add-product="addProduct({ type: 'basic', parent: $event })"
                @add-service="addProduct({ type: 'service', parent: $event })"
                @edit-attachments="openOrderItemEditModalAttachments"
                @create-producable="createProducable"
                @edit-producable="editProducable"
                @change="updateOrderItem"
                @remove="removeOrderItem"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <h3 class="uppercase font-medium text-lg">Schedule</h3>
          <BaseToggle v-model="orderType" />
        </div>
        <OrderBillingScheduleForm
          v-if="data.type === 'client_subscription_order'"
          :billing-period="data.billingPeriod"
          :billing-interval="data.billingInterval"
          :billing-length="data.billingLength"
          @update:billing-period-value="data.billingPeriod = $event"
          @update:billing-interval-value="data.billingInterval = $event"
          @update:billing-length-value="data.billingLength = $event"
        />
      </div>

      <div class="flex">
        <div
          v-if="canDelete"
          class="py-6 pl-6 bg-gray-100 ml-auto"
          :class="{
            'rounded pr-6': confirmDelete,
            'rounded-l': !confirmDelete,
          }"
        >
          <div v-if="confirmDelete" key="1" class="flex space-x-4 justify-end items-baseline">
            <p>Are you sure you?</p>
            <BaseButton
              ghost
              key="cancelAction"
              :disabled="submitting"
              @click="confirmDelete = false"
            >
              <span class="px-4 capitalize">cancel</span>
            </BaseButton>
            <BaseButton key="deleteAction" danger :disabled="submitting" @click="doDeleteOrder">
              <span class="px-4 capitalize">delete</span>
            </BaseButton>
          </div>
          <div v-else key="2" class="flex space-x-4 justify-end items-baseline">
            <BaseButton
              v-if="canDelete"
              key="confirmAction"
              ghost
              :disabled="submitting"
              @click="confirmDelete = true"
            >
              <span class="px-4 capitalize">delete</span>
            </BaseButton>
          </div>
        </div>
        <div
          v-if="!confirmDelete"
          class="p-6 bg-gray-100"
          :class="{
            'rounded ml-auto': !canDelete,
            'rounded-r': canDelete,
          }"
        >
          <div class="flex -mx-6 items-baseline">
            <div class="flex-none px-6">
              <BaseButton
                success
                :disabled="invalid || submitting || !hasOrderItems"
                @click="handleSubmit(submit)"
              >
                <span class="px-4 capitalize">Submit</span>
              </BaseButton>
            </div>
            <div class="flex-none px-6">Total</div>
            <div class="flex-none px-6 text-right border-l">
              <span class="font-medium">{{ totalPrice }}</span> &euro;
            </div>
          </div>
        </div>
      </div>
    </ValidationObserver>

    <portal v-if="modal.visible" to="app-modal">
      <component :is="modal.component" v-bind="modal.props" v-on="modal.on" />
    </portal>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import DetailsForm from 'Components/Order/Form.vue';
import ModalProjectCampaignCreate from 'Components/ProjectCampaign/ModalCreate.vue';
import ModalCampaign from 'Components/Campaigns/CampaignModal.vue';
import ModalOrderItemAttachments from 'Components/OrderItem/OrderItemModal.vue';
import ConvertOrderToInvoiceForm from 'Forms/ConvertOrderToInvoiceForm.vue';
import { entityComputed } from '@/store/helper';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';
import OrderItem from './orderForm/OrderItem.vue';
import ModalOrderItemEditSpecs from './orderForm/ModalOrderItemEditSpecs.vue';
import ModalProduct from './orderForm/ModalProduct.vue';
import ModalOrderProjectVue from './ModalOrderProject.vue';
import OrderFormOrderItemsToolbar from './OrderFormOrderItemsToolbar.vue';
import OrderBillingScheduleForm from './OrderBillingScheduleForm.vue';

const orderLineWithDefaults = ({
  uuid = null,
  id = null,
  name = '',
  status = null,
  price = 0,
  orderUuid = null,
  parentLineId = null,
  parentOrderItemUuid = null,
  projectId = null,
  requirements = null,
  orderableType = null,
  orderableUuid = null,
  producableType = null,
  producableId = null,
  campaign = null,
} = {}) => ({
  uuid,
  name,
  status,
  orderUuid,
  pricePerUnit: price,
  quantity: 1,
  parentOrderItemUuid,
  requirements,
  tempId: id,
  tempParentId: parentLineId,
  orderableType,
  orderableUuid,
  producableType,
  producableId,
  projectId,
  campaign,
});

export default {
  mixins: [veeValidateErrorsMixin],

  components: {
    DetailsForm,
    OrderItem,
    ModalOrderItemEditSpecs,
    ModalProduct,
    ModalOrderProject: ModalOrderProjectVue,
    ModalOrderItemAttachments,
    ModalProjectCampaignCreate,
    ModalCampaign,
    OrderFormOrderItemsToolbar,
    ConvertOrderToInvoiceForm,
    OrderBillingScheduleForm,
  },

  props: {
    action: {
      type: Function,
      default: () => {
        throw new Error('action is not defined');
      },
    },
    order: {
      type: Object,
      default: null,
    },
    orderItems: {
      type: Array,
      default: () => [],
    },
    supplierOptions: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    submitting: false,
    confirmDelete: false,
    data: {
      name: '',
      supplierUuid: null,
      details: '',
      orderItems: [],
      projectUuid: null,
      type: 'client_order',
      billingPeriod: 'month',
      billingInterval: 1,
      billingLength: 12,
    },
    specsVisible: false,
    modal: {
      visible: false,
      component: null,
      props: {},
      on: {},
    },
  }),

  computed: {
    ...entityComputed,
    ...mapGetters('ui/orders', ['included']),
    ...mapState('entities', {
      clientRelation({ accountRelations }) {
        return accountRelations.byId[this.data.clientUuid] || null;
      },
      project({ projects }) {
        if (!this.data.projectUuid) return null;

        return projects.byId[this.data.projectUuid] || null;
      },
    }),
    orderType: {
      get() {
        return this.data.type === 'client_subscription_order';
      },
      set(value) {
        this.data.type = value ? 'client_subscription_order' : 'client_order';
      },
    },
    totalPrice() {
      return this.data.orderItems.reduce((acc, cur) => acc + cur.quantity * cur.pricePerUnit, 0);
    },
    baseId() {
      const sample = [0, ...this.data.orderItems.map((item) => item.tempId).filter((x) => x)];
      const max = Math.max(...sample);
      return max;
    },
    canDelete() {
      const { can = null } = this.order || {};

      if (can && can.delete) {
        return true;
      }

      return false;
    },
    hasOrderItems() {
      return this.data.orderItems.length > 0;
    },
    hasProject() {
      return this.project && typeof this.order.projectUuid === 'string';
    },
    hasProducables() {
      return this.orderItems.some(
        ({ producableType, producableId }) =>
          typeof producableType === 'string' && typeof producableId === 'string'
      );
    },
    sellerAccountId() {
      return this.order?.accountUuid || '';
    },
    customerAccountId() {
      return this.clientRelation?.related_account_uuid || '';
    },
  },

  watch: {
    orderItems() {
      this.data.orderItems.splice(0, this.data.orderItems.length);
      this.setDefaultDataFromExistingOrder();
    },
  },

  created() {
    this.setup();
  },

  methods: {
    ...mapActions('ui/orders', ['deleteOrder']),
    setup() {
      if (this.order) {
        this.setDefaultDataFromExistingOrder();
      } else {
        this.setDefaultDataFromLocalStorage();
      }
    },
    setDefaultDataFromLocalStorage() {
      const orderLines = JSON.parse(window.localStorage.getItem('orderLines'));

      window.localStorage.removeItem('orderLines');

      if (orderLines instanceof Array === false) return;

      if (!orderLines.length) return;

      orderLines.forEach((orderLine) => {
        this.data.orderItems.push(orderLineWithDefaults(orderLine));
      });

      if (this.data.orderItems.length) {
        this.data.name = this.data.orderItems[0].name;
      }
    },
    setDefaultDataFromExistingOrder() {
      if (!this.order) return;

      const order = JSON.parse(JSON.stringify(this.order));

      const {
        name,
        type,
        billingInterval,
        billingPeriod,
        billingLength,
        order_status: orderStatus,
        supplierUuid,
        clientUuid,
        assigneeUuid,
        contactPointUserId,
        projectUuid,
        deliveryAt,
        details,
        priorityHigh,
        published,
        statusId,
        tags,
      } = order;

      Object.assign(this.data, {
        name,
        type,
        billingInterval,
        billingPeriod,
        billingLength,
        order_status: orderStatus,
        supplierUuid,
        assigneeUuid,
        contactPointUserId,
        clientUuid,
        projectUuid,
        deliveryAt,
        details,
        priorityHigh,
        published,
        statusId,
        tags,
      });

      if (this.orderItems.length === 0) return;

      const reOrderedOrderItems = this.reOrderOrderItems(this.orderItems);

      reOrderedOrderItems.forEach((orderItem) => {
        this.data.orderItems.push(orderLineWithDefaults(orderItem));
      });
    },
    reOrderOrderItems(orderItems) {
      const bucket = orderItems.slice(0);
      const result = [];

      let successivePostpones = 0;

      const pushNextToParent = (subject) => {
        let indexOfParent = -1;

        if (subject.parentOrderItemUuid) {
          indexOfParent = result.map((x) => x.uuid).indexOf(subject.parentOrderItemUuid);

          if (indexOfParent >= 0) {
            result.splice(indexOfParent + 1, 0, subject);
            return;
          }
        } else {
          indexOfParent = result.map((x) => x.id).indexOf(subject.tempParentId);

          if (indexOfParent >= 0) {
            result.splice(indexOfParent + 1, 0, subject);
            return;
          }
        }

        bucket.push(subject);
        successivePostpones += 1;
      };

      const pushNextToLastSibling = (subject) => {
        let indexOfLastSibling = -1;

        if (subject.parentOrderItemUuid) {
          indexOfLastSibling = result
            .map((x) => x.parentOrderItemUuid)
            .filter((x) => x)
            .lastIndexOf(subject.parentOrderItemUuid);

          if (indexOfLastSibling >= 0) {
            result.splice(indexOfLastSibling + 1, 0, subject);

            return;
          }
        } else {
          indexOfLastSibling = result
            .map((x) => x.tempParentId)
            .filter((x) => x)
            .lastIndexOf(subject.tempParentId);

          if (indexOfLastSibling >= 0) {
            result.splice(indexOfLastSibling + 1, 0, subject);

            return;
          }
        }

        pushNextToParent(subject);
      };

      const pushToEnd = (subject) => {
        if (!subject.parentOrderItemUuid && !subject.tempParentId) {
          result.push(subject);

          return;
        }

        pushNextToLastSibling(subject);
      };

      while (bucket.length && successivePostpones < bucket.length) {
        const subject = bucket.shift();

        pushToEnd(subject);
      }

      return result;
    },
    updateOrderData(data) {
      this.data = { ...this.data, ...data };
    },
    addLine(parentOrderItem = null) {
      const { uuid = null, tempId = null } = parentOrderItem || {};
      this.data.orderItems.push(
        orderLineWithDefaults({
          id: this.baseId + 1,
          name: '',
          ...(uuid ? { parentOrderItemUuid: uuid } : {}),
          ...(tempId ? { parentLineId: tempId } : {}),
        })
      );
    },
    addProduct({ type, parent = null }) {
      this.modal.component = 'ModalProduct';
      this.modal.props = {
        baseId: this.baseId,
        orderItem: JSON.parse(JSON.stringify(parent || {})),
        options: {
          type,
        },
      };
      this.modal.on = {
        add: this.addOrderLines,
        closed: this.resetModal,
      };
      this.modal.visible = true;
    },
    createProducable({ orderItem, product }) {
      if (product.producesModelType !== 'campaigns') {
        return;
      }

      const originalOrderItem = this.orderItems.find((o) => o.uuid === orderItem.uuid);
      if (orderItem.projectId !== originalOrderItem?.projectId) {
        alert('Please save your changes before creating an ad plan');
        return;
      }

      this.openProjectCampaignCreateModal(orderItem);
    },
    editProducable({ producableType, producableId }) {
      if (producableType === 'campaigns') {
        this.openCampaignModal(producableId);
      }
    },
    openOrderItemEditModalAttachments(orderItem) {
      this.modal.component = 'ModalOrderItemAttachments';
      this.modal.props = {
        orderItemUuid: orderItem.uuid,
      };
      this.modal.on = {
        close: this.resetModal,
      };
      this.modal.visible = true;
    },
    openProjectCampaignCreateModal(orderItem) {
      this.modal.component = 'ModalProjectCampaignCreate';
      this.modal.props = {
        closeOnSuccess: true,
        campaign: {
          name: this.order.name,
          orderItem: orderItem.uuid,
        },
        action: async (params) => {
          try {
            await this.$store.dispatch({
              type: 'ui/projectCampaigns/store',
              projectId: orderItem.projectId,
              params,
            });
          } catch (error) {
            /** handle error */
          }
        },
      };
      this.modal.on = {
        closed: this.resetModal,
      };
      this.modal.visible = true;
    },
    openCampaignModal(campaignId) {
      this.modal.component = 'ModalCampaign';
      this.modal.props = {
        campaignId,
      };
      this.modal.on = {
        'campaign-modal-close': this.resetModal,
      };
      this.modal.visible = true;
    },
    resetModal() {
      this.modal.visible = false;
      this.modal.component = null;
      this.modal.props = {};
      this.modal.on = {};
    },
    removeOrderItem(orderItemToRemove) {
      const orderItemsToReject = [
        orderItemToRemove,
        ...this.orderItemChildrenToRemove(orderItemToRemove),
      ];

      this.data.orderItems = [
        ...this.data.orderItems.filter((orderItem) => {
          if (orderItem.uuid) {
            return (
              orderItemsToReject.findIndex(
                (rejectedOrderItem) => rejectedOrderItem.uuid === orderItem.uuid
              ) === -1
            );
          }

          return (
            orderItemsToReject.findIndex(
              (rejectedOrderItem) => rejectedOrderItem.tempId === orderItem.tempId
            ) === -1
          );
        }),
      ];
    },
    orderItemChildrenToRemove(orderItem) {
      const children = [
        ...this.data.orderItems.filter((childOrderItem) => {
          if (orderItem.uuid) {
            return childOrderItem.parentOrderItemUuid === orderItem.uuid;
          }

          return childOrderItem.tempParentId === orderItem.tempId;
        }),
      ];

      return [
        ...children,
        ...children.reduce((acc, cur) => [...acc, ...this.orderItemChildrenToRemove(cur)], []),
      ];
    },
    addOrderLines(orderLines) {
      orderLines
        .map((orderLine) => {
          if (orderLine.parentLineId) return orderLine;

          const { uuid = null, tempId = null } = this.modal.props.orderItem || {};

          if (uuid) {
            return { ...orderLine, parentOrderItemUuid: uuid };
          }

          return { ...orderLine, parentLineId: tempId };
        })
        .forEach((orderLine) => {
          this.data.orderItems.push(orderLineWithDefaults(orderLine));
        });
    },
    updateOrderItem(updatedOrderItem) {
      const index = this.data.orderItems.findIndex((orderItem) =>
        updatedOrderItem.uuid
          ? orderItem.uuid === updatedOrderItem.uuid
          : orderItem.tempId === updatedOrderItem.tempId
      );

      if (index >= 0) {
        this.data.orderItems.splice(index, 1, updatedOrderItem);
      }
    },

    submit() {
      if (this.data.orderItems.length === 0) return;

      this.submitting = true;

      this.action({ params: { ...this.data } })
        .then(() => {
          this.submitting = false;
        })
        .catch(({ status = null, data = {} } = {}) => {
          if (status === HTTP_UNPROCESSABLE_ENTITY) {
            this.observer = this.$refs.observer;
            this.addServerErrors(data.errors);
          }

          this.submitting = false;
        });
    },
    doDeleteOrder() {
      const { uuid: orderId } = this.order;
      this.submitting = true;
      this.deleteOrder({ orderId })
        .then(() => {
          this.submitting = false;
          this.$router.replace({
            name: 'accounts.orders',
            params: { accountId: this.$route.params.accountId },
          });
        })
        .catch(() => {
          this.submitting = false;
        });
    },
  },
};
</script>
